import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@shared/contexts/LanguageContext";
import { addToHistory } from "@shared/lib/history";
import { logVisitorActivity } from "@shared/lib/visitorTracking";
import axios from "axios";
import { Menu, ArrowLeft } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { toast } from "sonner";
import PreferencesPanel from "@visitor/components/PreferencesPanel";
import FontSizeControl from "@visitor/components/FontSizeControl";
import NarrationControl from "@visitor/features/exhibit-detail/NarrationControl";
import MediaGallery from "@visitor/features/exhibit-detail/MediaGallery";

const DX_LOGO_URL =
  "https://cdn.art-mate.net/uploads/artmate/202502/thumbnail/20250228_160227_1YqJgAgCUS_p_300_0.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExhibitDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, fontSize, t } = useLanguage();
  const [exhibit, setExhibit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Track which navigation key we've logged for to prevent duplicates
  const lastLoggedKey = useRef(null);

  // Fetch exhibit data
  const fetchExhibit = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/exhibits/${id}`);
      setExhibit(response.data);
      addToHistory(id);

      // Track this page visit only once per navigation
      const currentKey = `${id}-${location.key}`;
      if (lastLoggedKey.current !== currentKey) {
        lastLoggedKey.current = currentKey;
        const exhibitData = response.data;
        logVisitorActivity(
          "exhibit",
          exhibitData.id,
          exhibitData.code,
          exhibitData.titleTc || exhibitData.titleEn,
        );
      }
    } catch (error) {
      toast.error("Exhibit not found or not available");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!language || !["en", "tc", "sc"].includes(language)) {
      localStorage.setItem(
        "redirectPath",
        location.pathname + location.search + location.hash,
      );
      navigate("/");
    }
  }, [location, navigate]);

  // Fetch exhibit when id changes OR when navigating back/forward
  useEffect(() => {
    fetchExhibit();
  }, [id, location.key]);

  // F-F single-playback: only one source plays at a time (narration vs a
  // gallery audio track or video). Media "play" events don't bubble but DO
  // fire capture-phase listeners on ancestors, so one document-level capture
  // listener pauses every other <audio>/<video> and cancels any browser-TTS
  // narration the moment a track starts. The TTS→media direction (starting TTS
  // while a track plays) is handled by NarrationControl's onPointerDownCapture.
  const pauseAllAudio = () => {
    document.querySelectorAll("audio, video").forEach((el) => el.pause());
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  };

  useEffect(() => {
    const onPlayCapture = (e) => {
      const target = e.target;
      if (!(target instanceof HTMLMediaElement)) return;
      document.querySelectorAll("audio, video").forEach((el) => {
        if (el !== target) el.pause();
      });
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
    document.addEventListener("play", onPlayCapture, true);
    return () => document.removeEventListener("play", onPlayCapture, true);
  }, []);

  const getLocalizedText = (field) => {
    if (!language) {
      localStorage.setItem(
        "redirectPath",
        location.pathname + location.search + location.hash,
      );
      navigate("/");
    }

    if (!exhibit) return "";

    if (language === "tc") {
      return exhibit[`${field}Tc`] || exhibit[`${field}En`] || "";
    } else if (language === "sc") {
      return exhibit[`${field}Sc`] || exhibit[`${field}En`] || "";
    } else {
      return exhibit[`${field}En`] || exhibit[`${field}Tc`] || "";
    }
  };

  const getFontSizeClass = () => {
    if (fontSize === "small") return "text-base";
    if (fontSize === "large") return "text-2xl";
    return "text-lg";
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-black"
        data-testid="loading-state"
      >
        <div className="animate-pulse text-white/60">Loading...</div>
      </div>
    );
  }

  if (!exhibit) {
    return null;
  }

  const title = getLocalizedText("title");
  const description = getLocalizedText("description");

  return (
    <div
      className="min-h-screen bg-black pb-28"
      data-testid="exhibit-detail-page"
    >
      {/* Header */}
      <div className="sticky top-0 bg-black/90 backdrop-blur border-b border-white/10 z-10">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between relative">
          <img
            src={DX_LOGO_URL}
            alt="DX Logo"
            className="w-28 object-contain"
          />
          {/* <h1 className="absolute left-1/2 -translate-x-1/2 text-2xl font-heading font-bold text-white">
            {t({ en: 'Exhibit', tc: '展品', sc: '展品' })}
          </h1> */}
          <button
            onClick={() => setPreferencesOpen(true)}
            className="text-white hover:bg-white/10 rounded-lg p-2"
            data-testid="open-preferences"
          >
            <Menu className="w-8 h-8" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Image Hero */}
      {exhibit.coverImageId && (
        <div className="relative w-full h-[40vh] bg-white/5">
          <img
            src={`${API}/assets/${exhibit.coverImageId}`}
            alt={title}
            className="w-full h-full object-cover"
            data-testid="exhibit-image"
          />

          {/* Overlay back button: circular, half-transparent */}
          <button
            onClick={() => {
              const previousUrl = document.referrer;
              const isSameSite =
                previousUrl && previousUrl.startsWith(window.location.origin);

              if (isSameSite) {
                const previousPath = new URL(previousUrl).pathname;
                if (previousPath !== "/") {
                  navigate(-1);
                  return;
                }
              }

              navigate("/qr-instructions");
            }}
            className="absolute top-4 left-4 z-5 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/50 hover:bg-white/60 backdrop-blur-sm"
            aria-label={t({ en: "Back", tc: "返回", sc: "返回" })}
            data-testid="image-back-button"
          >
            <ArrowLeft className="w-5 h-5 text-black" strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Content - Double margin */}
      <div className="px-12 pb-20 py-8 max-w-2xl mx-auto space-y-6">
        {/* Title */}
        <h1
          className="text-3xl md:text-4xl font-heading font-bold tracking-tight text-white"
          data-testid="exhibit-title"
        >
          {title}
        </h1>

        {/* Audio Player and Font Size Control - Same row */}
        <div className="flex items-center gap-4 bg-white/5 rounded-xl p-4">
          <div className="flex-1">
            {/* <NewAudioPlayer audioUrl={`${getLocalizedText('voice_url')}`}/>
          </div>
          <div className="flex-shrink-0 border-l border-white/20 pl-4"> */}
            <FontSizeControl />
          </div>
        </div>

        {/* Description */}
        <div
          className="prose prose-lg max-w-none"
          data-testid="exhibit-description"
        >
          <p
            className={`${getFontSizeClass()} text-justify leading-relaxed text-white/90 whitespace-pre-wrap`}
          >
            {description}
          </p>
        </div>

        {/* Media gallery (F-F.2) — supporting audio, language-filtered */}
        <MediaGallery
          media={exhibit.media}
          api={API}
          language={language}
          t={t}
        />
      </div>

      {/* Bottom Navigation - Only Done button with larger font */}
      <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-white/10 p-4">
        {/* <div className="max-w-2xl mx-auto"> */}
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-x-4 gap-y-3">
          {/* <Button
            onClick={() => {
              if (location.state?.from) {
                navigate(location.state.from);
              } else {
                navigate('/qr-instructions');
              }
            }}
            className="w-full h-16 text-2xl font-bold bg-white text-black hover:bg-white/90"
            data-testid="done-button"
          >
            {t({ en: 'Back', tc: '返回', sc: '返回' })}
          </Button> */}
          <div className="flex-1">
            <NarrationControl
              entity={exhibit}
              language={language}
              text={description}
              api={API}
              onTtsStart={pauseAllAudio}
            />
          </div>
          <div className="w-full md:w-auto">
            <Button
              onClick={() => {
                if (location.state?.from) {
                  navigate(location.state.from);
                } else {
                  navigate("/qr-instructions");
                }
              }}
              className="w-full h-16 text-2xl font-bold bg-white text-black hover:bg-white/90"
              data-testid="done-button"
            >
              {t({ en: "Back", tc: "返回", sc: "返回" })}
            </Button>
          </div>
        </div>
      </div>

      {/* Preferences Panel */}
      <PreferencesPanel
        isOpen={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </div>
  );
};

export default ExhibitDetail;
